/* eslint-disable max-len */
import {
	useState, useEffect, useContext, useRef
} from 'react';

import classNames from 'classnames';
import { useMsal } from '@azure/msal-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { navigate } from 'gatsby';
import { useLocalStorage } from '../../utils/hooks';
import { formatPrice } from '../../utils/format-price';
import { readJsonSafe } from '../../utils';
import { localStorageClassCheckoutKey } from '../../utils/store-context-hooks/checkout-hook';

import {
	Accordion, AccordionItem, AccordionControl, AccordionPanel
} from '../Accordion';
import Modal from '../Modal';
import Button from '../Button';
import LineItem from '../LineItem';

import { StoreContext } from '../../context/store-context';
import { gtmCheckout } from '../../utils/enhanced-ecommerce';
import renderIcon from '../../utils/renderIcon';
import { Tabs, Tab } from '../Tabs';

const schemaDiscount = yup.object({
	discountCode: yup.string().required('*Required'),
	customerEmail: yup.string().email('Invalid email address').required('*Required'),
});

const ns = `cart-flyout`;

// Build the cart-error modal payload from whatever the draft-order API returned.
// Prefers the backend's structured details; falls back to a generic message
// when the body was missing or unparseable.
const buildDraftOrderError = payload => {
	// Known structured error: specific items are no longer available.
	if (payload?.error_code === 'ITEMS_UNAVAILABLE' && Array.isArray(payload.unavailable_items)) {
		const titles = payload.unavailable_items.map(item => item.class_title).filter(Boolean);
		const isPlural = titles.length > 1;

		return {
			visible: true,
			errorHeading: 'Some items are no longer available',
			errorBody: titles.length
				? (
					<>
						{`The following ${ isPlural ? 'items are' : 'item is' } no longer available:`}
						{titles.map((title, index) => (
							<span key={`${ title + index }`} className={`${ ns }__cart-error-item`}>{title}</span>
						))}
						<strong className={`${ ns }__cart-error-action`}>
							{`Please remove ${ isPlural ? 'them' : 'it' } from your cart and try again.`}
						</strong>
					</>
				)
				: (payload.message || 'Some items in your cart are no longer available.'),
			errorEmail: '',
		};
	}

	// Any other backend-provided message. Guard against thrown Error objects
	// (network/unexpected failures) whose .message is a technical string we
	// don't want to show the user — those fall through to the generic message.
	if (payload && !(payload instanceof Error) && payload.message) {
		return {
			visible: true,
			errorHeading: 'Unable to complete checkout',
			errorBody: payload.message,
			errorEmail: '',
		};
	}

	// Body was empty / not JSON (gateway error, timeout, etc.).
	return {
		visible: true,
		errorHeading: 'Error creating draft order',
		errorBody: 'Please try again later.',
		errorEmail: '',
	};
};

const CartFlyout = () => {
	const { accounts } = useMsal();

	const {
		checkout,
		classCheckout,
		lastAdded,
		setCheckoutLoadingState,
		checkEventClassesAvailability,
		loading,
		isCartFlyoutOpen,
		cartFlyoutClose,
		isShoppingCartOffline,
		cartError,
		setCartError,
		affiliateFunc,
		// getUserIDToken,
		removeLineItems,
	} = useContext(StoreContext);

	const {
		register,
		handleSubmit,
		clearErrors,
		setError,
		reset,
		getValues,
		formState: {
			errors, isValid, isSubmitting, isSubmitSuccessful: isValidPromoCode,
		},
	} = useForm({
		resolver: yupResolver(schemaDiscount),
		mode: 'onTouched',
		defaultValues: {
			discountCode: '',
			customerEmail: '',
		},
	});

	const [selectedCartTab, setSelectedCartTab] = useState(null);
	const { value: draftOrdersIds, setValue: setDraftOrdersIds } = useLocalStorage('draftOrdersIds', []);

	const discountAccordionRef = useRef(null);

	// Determine cart type
	const emptyCart = !checkout.lineItems.length && !classCheckout.lineItems.length && !draftOrdersIds.length;
	const blendedCart = !!checkout.lineItems.length && !!(classCheckout.lineItems.length || draftOrdersIds.length);
	const singleCart = !emptyCart && !blendedCart;

	//	In a blended cart, default the active tab to Education, but activate the
	//	tab an item was just added to (products/events -> Products, class ->
	//	Education). Manual tab clicks are preserved — they don't change deps.
	useEffect(() => {
		if (!blendedCart) return;
		setSelectedCartTab({ key: lastAdded?.type || 'classes' });
	}, [blendedCart, lastAdded]); // eslint-disable-line

	const discount = +(checkout.cost?.checkoutChargeAmount?.amount - checkout?.cost?.totalAmount?.amount);

	const retrieveDraftOrder = async orderId => {
		cartFlyoutClose();
		navigate(`/order-review?orderId=${ orderId }`);
	};

	const renderLineItems = _checkout => {
		if (!_checkout.lineItems.length) return null;
		return (
			<ul className={`${ ns }__line-items`}>
				{_checkout.lineItems.map(item => {
					return (
						<LineItem
							key={item.id}
							item={item}
							checkout={_checkout}
						/>
					);
				})}
			</ul>
		);
	};

	const renderDraftOrders = () => {
		if (!draftOrdersIds.length) return null;

		return (
			<div className={`${ ns }__draft-orders`}>
				{draftOrdersIds.map(id => {
					return (
						<div key={id} className={`${ ns }__draft-order-id`}>
							<button
								type={'button'}
								onClick={() => retrieveDraftOrder(id)}
							>
								Retrieve Order #
								{id.split('/').pop()}
							</button>
						</div>
					);
				})}
			</div>
		);
	};

	//	Tab label with its item count inline, e.g. "Products (3)".
	const renderTabTitle = (label, count) => (count > 0 ? `${ label } (${ count })` : label);

	const getTabTitle = () => {
		const eventItems = checkout.lineItems.filter(item => /^event/.test(item.merchandise?.product?.productType));
		if (!eventItems.length) return 'Products';

		const isAllEvents = eventItems.length === checkout.lineItems.length;
		if (isAllEvents) return 'Events';

		return 'Products & Events';
	};

	const getCheckoutType = () => {
		if (singleCart) {
			return checkout.lineItems.length > 0 ? 'products' : 'classes';
		}
		if (blendedCart) {
			return selectedCartTab?.key || 'classes';
		}
		return null;
	};

	const isRenderCheckoutFooter = () => {
		if (emptyCart) return false;
		if (singleCart) return classCheckout.lineItems.length > 0 || checkout.lineItems.length > 0;
		if (blendedCart) return getCheckoutType() === 'classes' ? classCheckout.lineItems.length > 0 : checkout.lineItems.length > 0;
	};

	const getSubtotal = () => {
		const type = getCheckoutType();
		const costObj = type === 'products' ? checkout.cost : classCheckout.cost;
		if (!costObj) return null;
		return formatPrice(costObj.totalAmount.currencyCode, costObj.totalAmount.amount);
	};

	const getSubmitButtonText = () => {
		if (isSubmitting) return 'Applying…';
		if (isValidPromoCode) return 'Applied!';
		return 'Apply';
	};

	const getClassCartPayload = (discountCode = '') => {
		const classLineItems = classCheckout.lineItems
			.map(item => {
				return JSON.parse(item.attributes.find(attr => attr.key === '_line_item')?.value || '{}');
			});

		return {
			id: classCheckout.checkoutId,
			created_at: classCheckout.createdAt,
			discount_code: discountCode,
			lineItems: classLineItems,
		};
	};

	const onApplyDiscount = async data => {
		try {
			const classCart = getClassCartPayload(data.discountCode);

			const response = await fetch(process.env.GATSBY_DISCOUNT_VALIDATION_API, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(classCart),
			});
			if (!response.ok) throw response;

			const result = await response.json();

			if (result.valid) {
				discountAccordionRef.current.collapseAll();
			} else {
				setError('discountCode', { type: 'server', message: 'Invalid or unrecognized promo code!' });
			}
		} catch (error) {
			window.console.log('Error applying discount code:', error);
			setError('discountCode', { type: 'server', message: 'server error, please try again later or call us!' });
		}
	};

	const handleCheckout = async() => {
		const checkoutType = getCheckoutType();

		// Handle product checkout
		if (checkoutType !== 'classes') {
			await affiliateFunc();

			const { isSoldoutClasses } = await checkEventClassesAvailability();
			if (isSoldoutClasses) return;

			let checkoutUrl;
			if (accounts.length) {
				// Uses SSO multipass token to persist user (log in) to Shopify checkout
				// const idToken = await getUserIDToken();
				// checkoutUrl = `${ process.env.GATSBY_SSO_MULTI_PASS_TOKEN_CHECKOUT }?id_token=${ idToken }&return_to=${ checkout.checkoutUrl }`;
				checkoutUrl = checkout.checkoutUrl;
			} else {
				checkoutUrl = checkout.checkoutUrl;
			}

			return gtmCheckout(checkout.lineItems, checkoutUrl);
		}

		// Handle class checkout
		const classCart = getClassCartPayload(isValidPromoCode ? getValues('discountCode') : '');

		try {
			setCheckoutLoadingState(prev => ({ ...prev, [localStorageClassCheckoutKey]: true }));

			const draftOrderResponse = await fetch(process.env.GATSBY_CREATE_DRAFT_ORDER_API, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(classCart),
			});

			// Read the body even on a non-2xx — the backend sends its error
			// details (e.g. ITEMS_UNAVAILABLE) in the JSON body. readJsonSafe
			// returns null when the body is empty or not JSON.
			const draftOrderData = await readJsonSafe(draftOrderResponse);
			const orderId = draftOrderData?.draftOrderCreate?.draftOrder?.id;

			// Failure can be signalled by HTTP status, a { success: false }
			// body, or a missing order id. Throw the parsed payload so the
			// catch block builds the error message in one place.
			if (!draftOrderResponse.ok || draftOrderData?.success === false || !orderId) {
				throw draftOrderData || new Error(`Draft order failed: ${ draftOrderResponse.status }`);
			}

			// Remove line items from shopify class checkout after the draft order is created
			await removeLineItems(classCheckout.id, classCheckout.lineItems.map(item => item.id));
			retrieveDraftOrder(orderId);
			setDraftOrdersIds([...draftOrdersIds, orderId]);
		} catch (error) {
			// `error` is the backend payload for a handled failure, or a thrown
			// Error for a network/unexpected failure. buildDraftOrderError picks
			// the right message and falls back to a generic one.
			window.console.error('Error creating draft order:', error);
			setCartError(() => buildDraftOrderError(error));
		} finally {
			setCheckoutLoadingState(prev => ({ ...prev, [localStorageClassCheckoutKey]: false }));
		}
	};

	const rootClassnames = classNames({
		[`${ ns }`]: true,
		[`${ ns }--blended`]: blendedCart,
	});

	return (
		<Modal
			variant={`drawer`}
			className={`${ ns }__modal`}
			onRequestClose={() => { cartFlyoutClose(); }}
			isOpen={isCartFlyoutOpen}
		>
			<div className={rootClassnames}>
				<div className={`${ ns }__col ${ ns }__col--cart`}>
					<div className={`${ ns }__header`}>
						<h4>Cart</h4>
					</div>
					<div className={`${ ns }__body`}>
						{emptyCart && (
							<div className={`${ ns }__empty`}>
								<p className={`h3`}>
									Your cart is
									<br />
									currently empty.
								</p>
								<div>
									<Button
										type={'button'}
										className={'link link--cta'}
										onClick={() => { cartFlyoutClose(); }}
										variant={`cta`}
									>
										Continue Shopping
									</Button>
								</div>
							</div>
						)}
						{singleCart && (
							<>
								{renderLineItems(checkout)}
								{renderDraftOrders()}
								{renderLineItems(classCheckout)}
							</>
						)}
						{blendedCart && (
							<Tabs className={`${ ns }__tabs`} setExternalState={setSelectedCartTab}>
								<Tab
									key={`products`}
									title={renderTabTitle(getTabTitle(), checkout.lineItems.length)}
									selected={selectedCartTab?.key === 'products'}
								>
									{renderLineItems(checkout)}
								</Tab>
								<Tab
									key={`classes`}
									title={renderTabTitle('Classes', classCheckout.lineItems.length + draftOrdersIds.length)}
									selected={selectedCartTab?.key === 'classes'}
								>
									{renderDraftOrders()}
									{renderLineItems(classCheckout)}
								</Tab>
							</Tabs>
						)}
					</div>
					{isRenderCheckoutFooter() && (
						<div className={`${ ns }__footer`}>
							<div className={`${ ns }__totals`}>
								<div className={`${ ns }__totals-tax`}>
									<p>Shipping, taxes and discounts applied in checkout.</p>
								</div>
								{discount > 0 && getCheckoutType() !== 'classes' && (
									<div className={`${ ns }__totals-discount`}>
										<p>Discount</p>
										<p>
											-
											{' '}
											{formatPrice(
												checkout.cost.totalAmount.currencyCode,
												discount
											)}
										</p>
									</div>
								)}
								<div className={`${ ns }__totals-total`}>
									<p>Subtotal</p>
									<p>{getSubtotal()}</p>
								</div>
								{isValidPromoCode && (
									<div className={`${ ns }__totals-promo-code`}>
										<p>Promo Code to be Applied</p>
										<div>
											<button
												type={'button'}
												className={`${ ns }__remove-discount`}
												onClick={() => {
													reset();
												}}
											>
												{renderIcon('close')}
											</button>
											<span>{getValues('discountCode')}</span>
										</div>
									</div>
								)}
							</div>

							{/* Promo Code Validation Form */}
							{getCheckoutType() === 'classes' && (
								<Accordion
									ref={discountAccordionRef}
									className={`${ ns }__discount-accordion ${ isValidPromoCode ? `${ ns }__discount-accordion--hide` : '' }`}
								>
									<AccordionItem>
										<AccordionControl>
											<span className={`${ ns }__subtitle`}>Enter promo code</span>
											<span aria-hidden>
												{renderIcon('chevron')}
											</span>
										</AccordionControl>
										<AccordionPanel>
											<form
												onSubmit={handleSubmit(onApplyDiscount)}
												className={`${ ns }__discount-form`}
												onChange={() => isValidPromoCode && reset(undefined, {
													keepValues: true, keepIsSubmitSuccessful: false,
												})}
												noValidate
											>
												<div className={`form-group`}>
													<input
														{...register('discountCode', {
															onChange: () => clearErrors('discountCode'),
														})}
														className={`form-control ${ errors.discountCode ? 'error' : '' }`}
														type={'text'}
														placeholder={'Promo Code'}
														autoComplete={'off'}
														aria-invalid={!!errors.discountCode}
														aria-describedby={errors.discountCode ? 'promo-error' : undefined}
													/>

													{errors.discountCode && (
														<p id={'promo-error'} className={`form-error`}>
															{errors.discountCode.message}
														</p>
													)}
												</div>
												<div className={`form-group`}>
													<input
														{...register('customerEmail', {
															onChange: () => clearErrors('discountCode'),
														})}
														className={`form-control ${ errors.customerEmail ? 'error' : '' }`}
														type={'text'}
														placeholder={'Email'}
														autoComplete={'off'}
														aria-invalid={!!errors.customerEmail}
														aria-describedby={errors.customerEmail ? 'email-error' : undefined}
													/>

													{errors.customerEmail && (
														<p id={'email-error'} className={`form-error`}>
															{errors.customerEmail.message}
														</p>
													)}
												</div>

												<Button
													type={'submit'}
													variant={`green`}
													disabled={!isValid || isSubmitting || isValidPromoCode}
												>
													{getSubmitButtonText()}
												</Button>
											</form>
										</AccordionPanel>
									</AccordionItem>
								</Accordion>
							)}
							<div className={`${ ns }__checkout`}>
								<Button
									withArrow
									onClick={handleCheckout}
									disabled={loading || isShoppingCartOffline}
								>
									{loading ? 'Updating…' : 'Review Order & Checkout'}
								</Button>
								{isShoppingCartOffline && <p className={`${ ns }__offline`}>*Shopping Cart temporarily offline for maintenance</p>}
							</div>
						</div>
					)}
				</div>
			</div>
			{/* Modal for cart error */}
			<Modal
				// parentSelector={() => document.querySelector('.cart-flyout__modal')}
				className={`${ ns }__cart-error`}
				onRequestClose={() => setCartError(prev => ({ ...prev, visible: false }))}
				isOpen={!!cartError.visible}
			>
				<div className={`${ ns }__cart-error-heading`}>
					{renderIcon('cart-error')}
					{cartError.errorHeading ? <h4>{cartError.errorHeading}</h4> : <h4>Unable to add item to cart</h4>}
				</div>
				{cartError.errorBody && (
					<p>{cartError.errorBody}</p>
				)}
				{cartError.errorEmail && (
					<p>
						Please refresh the page and try again or
						{' '}
						<a
							className={'link'}
							href={`mailto:sitesupport@pilates.com?subject=Cart Error&body=${ cartError.errorEmail }`}
						>
							click here
						</a>
						{' '}
						to send us information which will help us to troubleshoot the issue.
					</p>
				)}
			</Modal>
		</Modal>
	);
};

export default CartFlyout;
